import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { BookOpen, Lock, Star, Search, Check, Sparkles, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { http, trackEvent } from "@/lib/api";
import { celebrate } from "@/lib/confetti";
import { Card, PageTitle, btnPrimary, fieldCls } from "@/components/portal/ui";
import { LessonView } from "@/components/portal/Lesson";
import { CourseOverview } from "@/components/portal/CourseOverview";

const STATUS_NEXT = { "Not Started": "In Progress", "In Progress": "Completed", "Completed": "Not Started" };

export default function Modules() {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(window.location.hash === "#final" ? "final" : null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(null); // module detail
  const [locked, setLocked] = useState(null); // locked module upsell
  const [params, setParams] = useSearchParams();
  const [course, setCourse] = useState(["ns-101", "is-201", "is-301", "is-401"].includes(params.get("course")) ? params.get("course") : "is-101");

  const load = () => http.get("/portal/modules").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { trackEvent("learning_center_view", {}, { once: true }); }, []);

  // Deep-link: /portal/resources?m=<key> auto-opens the module (from dashboard continue/nudge).
  useEffect(() => {
    const key = params.get("m");
    if (key && data && !open) {
      http.get(`/portal/modules/${key}`).then((r) => setOpen(r.data)).catch(() => {});
      params.delete("m"); setParams(params, { replace: true });
    }
  }, [data]); // eslint-disable-line

  const openKey = async (key) => {
    setOpen(null);
    try { const { data } = await http.get(`/portal/modules/${key}`); setOpen(data); window.scrollTo(0, 0); }
    catch (e) { toast.error("Could not open lesson"); }
  };
  if (!data) return <div className="text-[#6B665E]">Loading…</div>;
  if (open?.lesson) return <LessonView key={open.key} module={open} onClose={(t) => { setOpen(null); setTarget(typeof t === "string" ? t : null); load(); window.scrollTo(0, 0); }} onUpdated={load} onNavigate={openKey} />;

  const { modules, categories, lc_groups = [] } = data;
  const catLabel = Object.fromEntries(categories);
  const match = (m) => (!cat || m.lc === cat) &&
    (!q || (m.title + " " + m.summary).toLowerCase().includes(q.toLowerCase()));
  const visible = modules.filter((m) => m.unlocked && !m.lesson_no && match(m));
  const recommended = modules.filter((m) => m.unlocked && !m.lesson_no && m.recommended && match(m));
  const saved = modules.filter((m) => m.favorite && !m.lesson_no && match(m));
  const others = modules.filter((m) => m.unlocked && !m.lesson_no);

  const openModule = async (m) => {
    if (!m.unlocked) return;
    try {
      const { data } = await http.get(`/portal/modules/${m.key}`); setOpen(data);
      if (data.lesson) window.scrollTo(0, 0);
      trackEvent("module_viewed", { module_id: m.key, access_level: "unlocked", service_stage: m.category || "" });
      if (m.status === "Not Started") trackEvent("module_started", { module_id: m.key }, { once: true, key: m.key });
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Locked"); }
  };
  const setState = async (key, patch, refreshOpen) => {
    await http.put(`/portal/modules/${key}/state`, patch).catch(() => {});
    if (patch.status === "Completed") trackEvent("module_completed", { module_id: key });
    let fresh = null;
    try { fresh = (await http.get("/portal/modules")).data; setData(fresh); } catch (e) { load(); }
    if (refreshOpen) setOpen((o) => (o && o.key === key ? { ...o, ...patch } : o));
    if (patch.status === "Completed" && fresh) {
      const unlocked = fresh.modules.filter((m) => m.unlocked);
      if (unlocked.length && unlocked.every((m) => m.status === "Completed")) {
        celebrate();
        toast.success("All your modules are complete — beautifully done!");
        try { localStorage.setItem("isc_modules_celebrated", String(unlocked.length)); } catch (e) {}
      }
    }
  };

  const ModuleCard = ({ m }) => (
    <Card className="p-0 flex flex-col overflow-hidden" data-testid={`module-${m.key}`}>
      {m.image && (
        <div className="relative h-28 w-full overflow-hidden">
          <img src={m.image} alt="" loading="lazy" className={`w-full h-full object-cover ${m.unlocked ? "" : "grayscale opacity-60"}`} />
          {!m.unlocked && <div className="absolute inset-0 bg-[#1A1918]/30 grid place-items-center"><Lock size={18} className="text-white/90" /></div>}
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="eyebrow text-[#8A998C]">{catLabel[m.category]}</span>
          {m.unlocked && (
            <button onClick={() => setState(m.key, { favorite: !m.favorite })} data-testid={`module-fav-${m.key}`}
              className="text-[#C49A5A]" aria-label="Save">
              <Star size={16} className={m.favorite ? "fill-[#C49A5A]" : ""} />
            </button>
          )}
        </div>
        <h3 className="font-display text-lg text-[#1A1918] mt-2 leading-snug">{m.title}</h3>
        <p className="text-[#6B665E] text-sm mt-1 flex-1">{m.summary}</p>
        {m.unlocked ? (
          <div className="mt-3 flex items-center justify-between">
            <span className={`text-xs px-2 py-1 rounded-full ${m.status === "Completed" ? "bg-[#667A66]/15 text-[#4d6b4d]" : m.status === "In Progress" ? "bg-[#C49A5A]/15 text-[#8a6a2e]" : "bg-[#F3EFE9] text-[#8A998C]"}`}>{m.status}</span>
            <button onClick={() => openModule(m)} data-testid={`module-open-${m.key}`} className="text-[#9F634E] text-sm flex items-center gap-1 hover:underline">Open <ChevronRight size={14} /></button>
          </div>
        ) : (
          <button onClick={() => { setLocked(m); trackEvent("module_locked_click", { module_id: m.key, access_level: "locked", service_stage: m.category || "" }); }} data-testid={`module-locked-${m.key}`} className="mt-3 w-full flex items-center justify-between text-left group/lock">
            <span className="text-xs text-[#9F634E] flex items-center gap-1.5"><Lock size={12} /> {m.requires_label}</span>
            <span className="text-[#8A998C] text-xs flex items-center gap-1 group-hover/lock:text-[#9F634E]">Details <ChevronRight size={12} /></span>
          </button>
        )}
      </div>
    </Card>
  );

  return (
    <div>
      <PageTitle eyebrow="Coursework & Readings" title="Infant Sleep Learning Center"
        sub="Your lessons and readings. Open a lesson, study it, and mark it complete; your progress is saved to your student record." />

      <div className="flex flex-wrap gap-2 mb-8" role="tablist" data-testid="course-tabs">
        {[["is-101", "IS-101 · Foundations of Infant Sleep"], ["ns-101", "NS-101 · Naps, Wake Windows & Daily Rhythm"], ["is-201", "IS-201 · Night Wakings & Independent Settling"], ["is-301", "IS-301 · Sleep Regressions & Developmental Changes"], ["is-401", "IS-401 · Bedtime, Early Waking & the Crib Transition"]].map(([s, l]) => (
          <button key={s} role="tab" aria-selected={course === s} onClick={() => setCourse(s)} data-testid={`course-tab-${s}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${course === s ? "bg-[#172A3A] text-[#F5F2E9]" : "border border-[#E3DCCB] text-[#172A3A] hover:border-[#BDA677]"}`}>{l}</button>
        ))}
      </div>
      <CourseOverview key={course} slug={course} onOpen={openKey} refreshKey={data} scrollTo={target} onSwitch={(s) => { setCourse(s); setParams({ course: s }, { replace: true }); window.scrollTo(0, 0); }} />
      {others.length > 0 && <>
      <h2 className="font-academic font-semibold text-2xl text-[#172A3A] mt-14 mb-4">Additional Resources</h2>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A998C]" />
          <input data-testid="module-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the Learning Center…" className={`${fieldCls} pl-9`} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-full text-sm ${!cat ? "bg-[#9F634E] text-white" : "border border-[#E5E1D8] text-[#6B665E]"}`}>All</button>
        {lc_groups.map(([k, label]) => (
          <button key={k} data-testid={`lc-cat-${k}`} onClick={() => setCat(k)} className={`px-3 py-1.5 rounded-full text-sm ${cat === k ? "bg-[#9F634E] text-white" : "border border-[#E5E1D8] text-[#6B665E] hover:border-[#9F634E]/50"}`}>{label}</button>
        ))}
      </div>
      </>}

      {recommended.length > 0 && (
        <div className="mb-8">
          <div className="eyebrow text-[#8A998C] mb-3 flex items-center gap-1.5"><Sparkles size={14} /> Recommended for you</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="module-recommended">{recommended.map((m) => <ModuleCard key={m.key} m={m} />)}</div>
        </div>
      )}

      {saved.length > 0 && (
        <div className="mb-8">
          <div className="eyebrow text-[#8A998C] mb-3 flex items-center gap-1.5"><Star size={14} /> My Saved Resources</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="module-saved">{saved.map((m) => <ModuleCard key={m.key} m={m} />)}</div>
        </div>
      )}

      {lc_groups.filter(([k]) => visible.some((m) => m.lc === k)).map(([k, label]) => (
        <div key={k} className="mb-8">
          <div className="eyebrow text-[#8A998C] mb-3 flex items-center gap-1.5"><BookOpen size={14} /> {label}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`lc-group-${k}`}>{visible.filter((m) => m.lc === k).map((m) => <ModuleCard key={m.key} m={m} />)}</div>
        </div>
      ))}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(null)} data-testid="module-dialog">
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {open.image && <img src={open.image} alt="" className="w-full h-40 object-cover" />}
            <div className="p-7">
            <div className="eyebrow text-[#8A998C] mb-1">{catLabel[open.category]}</div>
            <h2 className="font-display text-2xl text-[#1A1918]">{open.title}</h2>
            <p className="text-[#6B665E] mt-3 text-[15px] leading-relaxed">{open.intro}</p>
            <ul className="mt-4 space-y-2">
              {open.points.map((p) => (
                <li key={p} className="flex gap-2 text-[15px] text-[#1A1918]"><Check size={16} className="text-[#9F634E] mt-1 shrink-0" /> {p}</li>
              ))}
            </ul>
            <div className="mt-6 flex items-center gap-3">
              <button data-testid="module-mark" onClick={() => setState(open.key, { status: STATUS_NEXT[open.status] }, true)} className={btnPrimary}>
                {open.status === "Completed" ? "Mark not started" : open.status === "In Progress" ? "Mark complete" : "Start this resource"}
              </button>
              <button onClick={() => setState(open.key, { favorite: !open.favorite }, true)} className="flex items-center gap-1.5 text-sm text-[#8a6a2e]">
                <Star size={16} className={open.favorite ? "fill-[#C49A5A] text-[#C49A5A]" : "text-[#C49A5A]"} /> {open.favorite ? "Saved" : "Save"}
              </button>
              <button onClick={() => setOpen(null)} className="ml-auto text-sm text-[#6B665E]">Close</button>
            </div>
            </div>
          </div>
        </div>
      )}

      {locked && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setLocked(null)} data-testid="module-locked-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {locked.image && (
              <div className="relative h-36 w-full overflow-hidden">
                <img src={locked.image} alt="" className="w-full h-full object-cover grayscale opacity-70" />
                <div className="absolute inset-0 bg-[#1A1918]/30 grid place-items-center"><Lock size={20} className="text-white/90" /></div>
              </div>
            )}
            <div className="p-7">
              <div className="eyebrow text-[#9F634E] mb-1 flex items-center gap-1.5"><Lock size={11} /> {locked.requires_label}</div>
              <h2 className="font-display text-2xl text-[#1A1918]">{locked.title}</h2>
              <p className="text-[#6B665E] mt-3 text-[15px] leading-relaxed">{locked.sample}</p>
              <p className="text-[#8A998C] text-sm mt-4">What this covers: {locked.summary}</p>
              <p className="text-[#8A998C] text-xs mt-4 leading-relaxed">The full module opens here once this service level is part of your journey.</p>
              <div className="mt-6 flex items-center gap-3">
                <Link to={locked.cta?.href || "/pricing"} data-testid="module-locked-cta" className={btnPrimary}>{locked.cta?.label || "Explore options"}</Link>
                <button onClick={() => setLocked(null)} className="ml-auto text-sm text-[#6B665E]">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
